import {
  Component,
  ChangeDetectionStrategy,
  input,
  effect,
  ElementRef,
  ViewChild,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import * as L from 'leaflet';

import { decodeGeohash, geohashToLatLng } from '@app/shared/utils/geohash.utils';
import { TOMASS_SUMMARY_I18N } from '../tomass-summary.i18n';
import { type MapMarkerData } from '../tomass-summary.types';

type MapMetricMode = 'scoreC' | 'seniorRatio' | 'volume';
type MapOverlayMode = 'scoreC' | 'seniorRatio' | 'volume';

interface DisplayCell extends MapMarkerData {
  precision: number;
}

const MIN_GEOHASH_PRECISION = 3;
const MAX_GEOHASH_PRECISION = 8;
const GEOHASH_PATTERN = /^[0-9bcdefghjkmnpqrstuvwxyz]{3,12}$/;

@Component({
  selector: 'app-tomass-regional-map',
  templateUrl: './tomass-regional-map.component.html',
  styleUrls: ['./tomass-regional-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TomassRegionalMapComponent implements AfterViewInit, OnDestroy {
  protected readonly i18n = TOMASS_SUMMARY_I18N.map;

  readonly markers = input<MapMarkerData[]>([]);
  readonly selectedGeohashes = input<string[]>([]);

  @ViewChild('mapContainer') private mapContainerRef?: ElementRef<HTMLDivElement>;

  private map: L.Map | null = null;
  private markerLayer: L.LayerGroup | null = null;
  private cellRenderer: L.Renderer | null = null;
  private legend: L.Control | null = null;
  private legendContainer: HTMLDivElement | null = null;
  private viewReady = false;
  private metricMode: MapMetricMode = 'scoreC';
  private overlayModes: Record<MapOverlayMode, boolean> = {
    scoreC: false,
    seniorRatio: false,
    volume: false,
  };
  private previousSelectionKey = '';

  constructor() {
    effect(() => {
      const data = this.markers();
      if (this.viewReady) {
        this.updateMarkers(data);
      }
    });

    effect(() => {
      const selection = this.selectedGeohashes();
      const normalizedSelection = selection
        .map((geohash) => this.normalizeGeohash(geohash))
        .filter((geohash): geohash is string => !!geohash)
        .sort((a, b) => a.localeCompare(b, 'en'));
      const currentSelectionKey = normalizedSelection.join('|');
      if (currentSelectionKey === this.previousSelectionKey) {
        return;
      }

      const hadSelection = this.previousSelectionKey.length > 0;
      this.previousSelectionKey = currentSelectionKey;

      if (this.viewReady) {
        this.updateMarkers(this.markers(), {
          preserveView: normalizedSelection.length === 0 && hadSelection,
          focusSelection: normalizedSelection.length > 0,
        });
      }
    });
  }

  get hasMarkers(): boolean {
    return this.markers().length > 0;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.initMap();
    // Leaflet needs a tick to calculate container size correctly
    setTimeout(() => {
      this.map?.invalidateSize();
      this.updateMarkers(this.markers());
    });
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  setMetricMode(mode: MapMetricMode): void {
    if (this.metricMode === mode) {
      return;
    }
    this.metricMode = mode;
    if (this.viewReady) {
      this.updateMarkers(this.markers(), { preserveView: true });
    }
  }

  isMetricMode(mode: MapMetricMode): boolean {
    return this.metricMode === mode;
  }

  toggleOverlay(mode: MapOverlayMode): void {
    this.overlayModes = {
      ...this.overlayModes,
      [mode]: !this.overlayModes[mode],
    };
    if (this.viewReady) {
      this.updateMarkers(this.markers(), { preserveView: true });
    }
  }

  isOverlayEnabled(mode: MapOverlayMode): boolean {
    return !!this.overlayModes[mode];
  }

  private initMap(): void {
    const el = this.mapContainerRef?.nativeElement;
    if (!el || typeof window === 'undefined') return;

    this.map = L.map(el, {
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView([36.5, 138.0], 5); // Japan center
    this.cellRenderer = L.canvas({ padding: 0.5 });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.map);

    this.map.on('zoomstart', () => {
      this.markerLayer?.clearLayers();
    });

    this.map.on('zoomend', () => {
      if (this.viewReady) {
        requestAnimationFrame(() => {
          this.updateMarkers(this.markers(), { preserveView: true });
        });
      }
    });

    this.markerLayer = L.layerGroup();
    this.map.addLayer(this.markerLayer);

    this.addLegend();
  }

  private updateMarkers(
    data: MapMarkerData[],
    options?: { preserveView?: boolean; focusSelection?: boolean },
  ): void {
    if (!this.map) return;
    const preserveView = options?.preserveView ?? false;
    const focusSelection = options?.focusSelection ?? false;

    if (this.markerLayer) {
      this.map.removeLayer(this.markerLayer);
    }
    this.markerLayer = L.layerGroup().addTo(this.map);

    if (data.length === 0) {
      this.updateLegend(0);
      return;
    }

    const zoom = this.map.getZoom();
    const displayCells = this.aggregateCellsByZoom(data, zoom);
    if (displayCells.length === 0) {
      this.updateLegend(0);
      return;
    }

    const selectedGeohashes = this.getNormalizedSelectedGeohashes();
    const hasSelection = selectedGeohashes.length > 0;
    const maxCount = Math.max(...displayCells.map((m) => m.totalCount), 1);
    let overallBounds: L.LatLngBounds | null = null;
    let selectedBounds: L.LatLngBounds | null = null;

    for (const m of displayCells) {
      const bounds = decodeGeohash(m.geohash);
      if (!bounds || this.shouldSkipBounds(bounds, m.precision)) {
        continue;
      }

      const rectangleBounds = L.latLngBounds(
        [bounds.minLat, bounds.minLng],
        [bounds.maxLat, bounds.maxLng],
      );
      const isSelected = this.isSelectedCell(m.geohash, selectedGeohashes);
      const fillColor = this.resolveFillColor(m, maxCount);
      const fillOpacity = this.resolveFillOpacity(m, maxCount, hasSelection, isSelected);
      const strokeColor = this.resolveStrokeColor(m, hasSelection, isSelected);
      const strokeWeight = this.resolveStrokeWeight(m, hasSelection, isSelected);

      const cell = L.rectangle(rectangleBounds, {
        renderer: this.cellRenderer ?? undefined,
        color: strokeColor,
        weight: strokeWeight,
        opacity: 0.95,
        fillColor,
        fillOpacity,
        className: 'tomass-grid-cell',
      });

      const dateStr = m.latestCreatedAt
        ? new Date(m.latestCreatedAt).toLocaleDateString('ja-JP')
        : '-';
      cell.bindPopup(this.buildPopupHtml(m, dateStr));
      this.markerLayer.addLayer(cell);
      if (isSelected) {
        cell.bringToFront();
        selectedBounds = selectedBounds
          ? selectedBounds.extend(rectangleBounds)
          : rectangleBounds;
      }

      if (this.shouldShowCountLabel(zoom, m.totalCount, maxCount, hasSelection, isSelected)) {
        const countLabel = L.marker([m.lat, m.lng], {
          interactive: false,
          icon: L.divIcon({
            className: 'tomass-grid-count-label',
            html: `<span>${m.totalCount}</span>`,
            iconSize: [34, 18],
            iconAnchor: [17, 9],
          }),
        });
        this.markerLayer.addLayer(countLabel);
      }

      overallBounds = overallBounds
        ? overallBounds.extend(rectangleBounds)
        : rectangleBounds;
    }

    if (!overallBounds) {
      this.updateLegend(maxCount);
      return;
    }

    if (focusSelection && selectedBounds) {
      this.map.fitBounds(selectedBounds, { padding: [40, 40], maxZoom: 11 });
    } else if (!preserveView) {
      if (displayCells.length === 1) {
        this.map.fitBounds(overallBounds, { padding: [80, 80], maxZoom: 11 });
      } else {
        this.map.fitBounds(overallBounds, { padding: [30, 30], maxZoom: 11 });
      }
    }

    this.updateLegend(maxCount);
  }

  private buildPopupHtml(m: MapMarkerData, dateStr: string): string {
    return `<div class="tomass-map-popup">
      <div><strong>GeoHash:</strong> ${m.geohash}</div>
      <div><strong>計測件数:</strong> ${m.totalCount}件</div>
      <div><strong>アカウント数:</strong> ${m.uniqueUserCount}件</div>
      <div><strong>A/B/C比率:</strong> ${m.scoreARatio}% / ${m.scoreBRatio}% / ${m.scoreCRatio}%</div>
      <div><strong>A/B/C件数:</strong> ${m.scoreA} / ${m.scoreB} / ${m.scoreC}</div>
      <div><strong>高齢比率(70+):</strong> ${m.seniorRatio}%</div>
      <div><strong>代表年代:</strong> ${m.avgAgeGroup}</div>
      <div><strong>最終計測日:</strong> ${dateStr}</div>
    </div>`;
  }

  private shouldShowCountLabel(
    zoom: number,
    count: number,
    maxCount: number,
    hasSelection: boolean,
    isSelected: boolean,
  ): boolean {
    if (hasSelection) {
      return isSelected;
    }
    if (this.metricMode === 'volume') {
      return zoom >= 6 || count >= Math.max(2, Math.ceil(maxCount * 0.28));
    }
    if (this.overlayModes.volume) {
      return zoom >= 7 || count >= Math.max(3, Math.ceil(maxCount * 0.4));
    }
    return zoom <= 5 && count >= Math.max(4, Math.ceil(maxCount * 0.5));
  }

  private getNormalizedSelectedGeohashes(): string[] {
    const selected = this.selectedGeohashes()
      .map((geohash) => this.normalizeGeohash(geohash))
      .filter((geohash): geohash is string => !!geohash);
    return Array.from(new Set(selected));
  }

  private isSelectedCell(cellGeohash: string, selectedGeohashes: string[]): boolean {
    if (selectedGeohashes.length === 0) {
      return false;
    }
    for (const selected of selectedGeohashes) {
      if (selected.startsWith(cellGeohash) || cellGeohash.startsWith(selected)) {
        return true;
      }
    }
    return false;
  }

  private aggregateCellsByZoom(cells: MapMarkerData[], zoom: number): DisplayCell[] {
    const precision = this.getDisplayPrecision(cells, zoom);
    const grouped = new Map<
      string,
      {
        geohash: string;
        precision: number;
        totalCount: number;
        uniqueUserCount: number;
        scoreA: number;
        scoreB: number;
        scoreC: number;
        scoreUnknown: number;
        seniorWeightedSum: number;
        latestCreatedAt: string;
        representativeAgeGroup: string;
        representativeAgeCount: number;
      }
    >();

    for (const cell of cells) {
      const normalizedGeohash = this.normalizeGeohash(cell.geohash);
      if (!normalizedGeohash) {
        continue;
      }

      const key = normalizedGeohash.slice(0, Math.min(precision, normalizedGeohash.length));
      // Mixed precision (too short geohash) can generate malformed large cells.
      if (key.length < MIN_GEOHASH_PRECISION) {
        continue;
      }

      const current = grouped.get(key) ?? {
        geohash: key,
        precision: Math.min(precision, normalizedGeohash.length),
        totalCount: 0,
        uniqueUserCount: 0,
        scoreA: 0,
        scoreB: 0,
        scoreC: 0,
        scoreUnknown: 0,
        seniorWeightedSum: 0,
        latestCreatedAt: '',
        representativeAgeGroup: cell.avgAgeGroup,
        representativeAgeCount: 0,
      };

      current.totalCount += cell.totalCount;
      current.uniqueUserCount += cell.uniqueUserCount;
      current.scoreA += cell.scoreA;
      current.scoreB += cell.scoreB;
      current.scoreC += cell.scoreC;
      current.scoreUnknown += cell.scoreUnknown;
      current.seniorWeightedSum += cell.seniorRatio * cell.totalCount;

      if (cell.totalCount >= current.representativeAgeCount) {
        current.representativeAgeGroup = cell.avgAgeGroup;
        current.representativeAgeCount = cell.totalCount;
      }

      if (
        cell.latestCreatedAt
        && (!current.latestCreatedAt || new Date(cell.latestCreatedAt) > new Date(current.latestCreatedAt))
      ) {
        current.latestCreatedAt = cell.latestCreatedAt;
      }

      grouped.set(key, current);
    }

    const result: DisplayCell[] = [];
    for (const entry of grouped.values()) {
      const center = geohashToLatLng(entry.geohash);
      if (!center) {
        continue;
      }
      const denominator = Math.max(1, entry.totalCount);
      result.push({
        geohash: entry.geohash,
        precision: entry.precision,
        lat: center.lat,
        lng: center.lng,
        totalCount: entry.totalCount,
        uniqueUserCount: entry.uniqueUserCount,
        scoreA: entry.scoreA,
        scoreB: entry.scoreB,
        scoreC: entry.scoreC,
        scoreUnknown: entry.scoreUnknown,
        scoreARatio: this.toPercent(entry.scoreA, denominator),
        scoreBRatio: this.toPercent(entry.scoreB, denominator),
        scoreCRatio: this.toPercent(entry.scoreC, denominator),
        seniorRatio: Math.round((entry.seniorWeightedSum / denominator) * 10) / 10,
        avgAgeGroup: entry.representativeAgeGroup,
        latestCreatedAt: entry.latestCreatedAt,
      });
    }

    return result.sort((a, b) => b.totalCount - a.totalCount);
  }

  private getDisplayPrecision(cells: MapMarkerData[], zoom: number): number {
    const desiredPrecision = this.getDesiredPrecisionByZoom(zoom);
    const validLengths = cells
      .map((cell) => this.normalizeGeohash(cell.geohash)?.length ?? 0)
      .filter((length) => length >= MIN_GEOHASH_PRECISION);

    if (validLengths.length === 0) {
      return desiredPrecision;
    }

    const availableMinPrecision = Math.max(MIN_GEOHASH_PRECISION, Math.min(...validLengths));
    return Math.min(desiredPrecision, availableMinPrecision);
  }

  private getDesiredPrecisionByZoom(zoom: number): number {
    if (zoom <= 4) return 3;
    if (zoom <= 8) return 4;
    if (zoom <= 10) return 5;
    if (zoom <= 12) return 6;
    if (zoom <= 14) return 7;
    return MAX_GEOHASH_PRECISION;
  }

  private shouldSkipBounds(
    bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
    precision: number,
  ): boolean {
    const { minLat, maxLat, minLng, maxLng } = bounds;
    if (
      !Number.isFinite(minLat)
      || !Number.isFinite(maxLat)
      || !Number.isFinite(minLng)
      || !Number.isFinite(maxLng)
    ) {
      return true;
    }

    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    if (latSpan <= 0 || lngSpan <= 0) {
      return true;
    }
    if (latSpan > 20 || lngSpan > 20) {
      return true;
    }

    const expected = this.expectedSpanByPrecision(precision);
    const latRatio = latSpan / expected.latSpan;
    const lngRatio = lngSpan / expected.lngSpan;
    if (
      latRatio < 0.5
      || latRatio > 1.8
      || lngRatio < 0.5
      || lngRatio > 1.8
    ) {
      return true;
    }

    return false;
  }

  private expectedSpanByPrecision(precision: number): { latSpan: number; lngSpan: number } {
    const normalizedPrecision = Math.max(1, Math.min(12, Math.floor(precision)));
    const bits = normalizedPrecision * 5;
    const lngBits = Math.ceil(bits / 2);
    const latBits = Math.floor(bits / 2);

    return {
      latSpan: 180 / (2 ** latBits),
      lngSpan: 360 / (2 ** lngBits),
    };
  }

  private normalizeGeohash(raw: unknown): string | null {
    if (typeof raw !== 'string') {
      return null;
    }

    const normalized = raw.trim().toLowerCase();
    if (!GEOHASH_PATTERN.test(normalized)) {
      return null;
    }
    return normalized;
  }

  private toPercent(value: number, total: number): number {
    if (total <= 0) {
      return 0;
    }
    return Math.round((value / total) * 1000) / 10;
  }

  private resolveFillColor(marker: MapMarkerData, maxCount: number): string {
    if (this.metricMode === 'scoreC') {
      return this.resolveScoreCColor(marker.scoreCRatio);
    }
    if (this.metricMode === 'seniorRatio') {
      return this.resolveSeniorColor(marker.seniorRatio);
    }
    return this.resolveVolumeColor(marker.totalCount, maxCount);
  }

  private resolveFillOpacity(
    marker: MapMarkerData,
    maxCount: number,
    hasSelection: boolean,
    isSelected: boolean,
  ): number {
    if (hasSelection) {
      return isSelected ? 0.88 : 0.16;
    }
    if (this.overlayModes.volume && this.metricMode !== 'volume') {
      const normalized = maxCount > 0 ? marker.totalCount / maxCount : 0;
      return Math.max(0.35, Math.min(0.9, 0.3 + normalized * 0.6));
    }
    return 0.72;
  }

  private resolveStrokeColor(
    marker: MapMarkerData,
    hasSelection: boolean,
    isSelected: boolean,
  ): string {
    if (hasSelection) {
      return isSelected ? '#0f172a' : 'rgba(148, 163, 184, 0.35)';
    }
    if (this.overlayModes.scoreC) {
      return this.resolveScoreCStrokeColor(marker.scoreCRatio);
    }
    return 'rgba(255, 255, 255, 0.85)';
  }

  private resolveStrokeWeight(
    marker: MapMarkerData,
    hasSelection: boolean,
    isSelected: boolean,
  ): number {
    if (hasSelection) {
      return isSelected ? 2.8 : 1;
    }
    const baseWeight = this.overlayModes.scoreC ? 1.8 : 1.2;
    if (this.overlayModes.seniorRatio) {
      if (marker.seniorRatio >= 60) return 4;
      if (marker.seniorRatio >= 40) return 3;
      if (marker.seniorRatio >= 20) return 2;
      return baseWeight;
    }
    return baseWeight;
  }

  private resolveScoreCColor(scoreCRatio: number): string {
    if (scoreCRatio <= 10) return '#22c55e';
    if (scoreCRatio <= 20) return '#84cc16';
    if (scoreCRatio <= 35) return '#f59e0b';
    return '#ef4444';
  }

  private resolveScoreCStrokeColor(scoreCRatio: number): string {
    if (scoreCRatio <= 10) return '#166534';
    if (scoreCRatio <= 20) return '#4d7c0f';
    if (scoreCRatio <= 35) return '#b45309';
    return '#b91c1c';
  }

  private resolveSeniorColor(seniorRatio: number): string {
    if (seniorRatio <= 20) return '#3b82f6';
    if (seniorRatio <= 40) return '#6366f1';
    if (seniorRatio <= 60) return '#8b5cf6';
    return '#d946ef';
  }

  private resolveVolumeColor(count: number, maxCount: number): string {
    const volumePercent = maxCount > 0 ? (count / maxCount) * 100 : 0;
    if (volumePercent <= 25) return '#94a3b8';
    if (volumePercent <= 50) return '#64748b';
    if (volumePercent <= 75) return '#475569';
    return '#1e293b';
  }

  private addLegend(): void {
    if (!this.map) return;

    const LegendControl = L.Control.extend({
      onAdd: () => {
        const div = L.DomUtil.create('div', 'tomass-map-legend') as HTMLDivElement;
        L.DomEvent.disableClickPropagation(div);
        this.legendContainer = div;
        this.updateLegend(0);
        return div;
      },
    });

    this.legend = new LegendControl({ position: 'bottomright' });
    this.legend.addTo(this.map);
  }

  private updateLegend(maxCount: number): void {
    if (!this.legendContainer) {
      return;
    }

    const metricLabel =
      this.metricMode === 'scoreC'
        ? this.i18n.metricScoreC
        : this.metricMode === 'seniorRatio'
          ? this.i18n.metricSeniorRatio
          : this.i18n.metricVolume;

    const items =
      this.metricMode === 'scoreC'
        ? [
          ['#22c55e', '0-10%'],
          ['#84cc16', '11-20%'],
          ['#f59e0b', '21-35%'],
          ['#ef4444', '36%+'],
        ]
        : this.metricMode === 'seniorRatio'
          ? [
            ['#3b82f6', '0-20%'],
            ['#6366f1', '21-40%'],
            ['#8b5cf6', '41-60%'],
            ['#d946ef', '61%+'],
          ]
          : [
            ['#94a3b8', '小'],
            ['#64748b', '中'],
            ['#475569', '大'],
            ['#1e293b', `最大 ${maxCount}件`],
          ];

    const overlayHints: string[] = [this.i18n.legendHintFill];
    if (this.overlayModes.scoreC) {
      overlayHints.push(this.i18n.legendHintStrokeScoreC);
    }
    if (this.overlayModes.seniorRatio) {
      overlayHints.push(this.i18n.legendHintStrokeSenior);
    }
    if (this.overlayModes.volume || this.metricMode === 'volume') {
      overlayHints.push(this.i18n.legendHintCount);
    }

    this.legendContainer.innerHTML = `
      <div class="tomass-map-legend__title">${metricLabel}</div>
      ${items
        .map(
          ([color, label]) =>
            `<span class="tomass-map-legend__item">
              <span class="tomass-map-legend__dot" style="background:${color}"></span>${label}
            </span>`,
        )
        .join('')}
      ${overlayHints.map((hint) => `<div class="tomass-map-legend__hint">${hint}</div>`).join('')}
    `;
  }

  private destroyMap(): void {
    this.legend?.remove();
    this.legend = null;
    this.legendContainer = null;
    this.markerLayer?.clearLayers();
    this.markerLayer = null;
    this.cellRenderer = null;
    this.map?.remove();
    this.map = null;
  }
}
